import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  BUFFER_COLLECTION_ID,
  DEFAULT_SAMPLES_PAGE_SIZE,
  TARIFF_DATASET_SYSTEM_KEY,
  clampSamplesPage,
  isQuotaFull,
  isReadOnlyCollection,
  type Collection,
  type CollectionDetectorBatchRunOutcome,
  type MediaSample,
  type PaginatedSamples,
} from '@membrana/media-library-service';
import {
  bindSamplePlaybackBlobReader,
  disposeSamplePlayback,
  selectSample,
  togglePlayPause,
  useSamplePlayback,
  useSamplePlaybackEscapeKey,
} from '@membrana/sample-playback-service';

import {
  canOfferMoveAll,
  isMoveAllSourceBuffer,
  type MoveAllPort,
} from '@/components/sample-library/MoveAllToCollectionDialog';

import {
  fetchMembraneCatalog,
  fetchMembraneNodes,
  patchCatalogSample,
  type MembraneCatalog,
  type MembraneCatalogSample,
  type MembraneNodeLibrary,
} from '@/api/sampleLibrary';
import { fetchMembraneMe } from '@/api/membrane';
import { useAuth } from '@/context/AuthContext';
import { catalogSampleToMedia } from '@/lib/catalogSampleAdapter';
import {
  DEFAULT_IMPORT_CLASS,
  type LibrarySelection,
} from '@/lib/cabinetSampleLibraryTypes';
import { invalidateCabinetMediaLibrary } from '@/lib/cabinetMediaLibrary';
import { downloadBlob, extensionFromMime } from '@/lib/downloadBlob';
import { useCabinetMediaLibrary } from '@/lib/useCabinetMediaLibrary';
import { useCabinetToast } from '@/lib/useCabinetToast';
import { runRemoteMutation } from '@/lib/remoteMutation';
import type { UpdateSampleLabelNotes } from '@membrana/media-library-service';

export type { LibrarySelection };

export function useCabinetSampleLibrary() {
  useSamplePlaybackEscapeKey();

  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [membraneId, setMembraneId] = useState<string | null>(null);
  const [catalog, setCatalog] = useState<MembraneCatalog | null>(null);
  const [nodes, setNodes] = useState<MembraneNodeLibrary[]>([]);
  const [selection, setSelection] = useState<LibrarySelection>({ kind: 'catalog' });
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [newCollectionName, setNewCollectionName] = useState('');
  const [mediaReloadNonce, setMediaReloadNonce] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  /**
   * Состояния сохранения подписи — ПО КАЖДОЙ пробе, именованные (#2110, b4 в кабинете).
   * Прежде один labelSavingId на весь модуль держал владельца: пока одна подпись едет,
   * следующую не тронуть. Теперь подписал — перешёл к следующей; исход виден у своей строки.
   */
  const [labelStates, setLabelStates] = useState<
    Record<string, { state: 'idle' | 'saving' | 'saved' | 'error'; detail?: string }>
  >({});
  const setLabelState = useCallback(
    (sampleId: string, next: { state: 'idle' | 'saving' | 'saved' | 'error'; detail?: string }) => {
      setLabelStates((prev) => ({ ...prev, [sampleId]: next }));
      if (next.state === 'saved') {
        // «Сохранено» — сигнал, не жилец.
        window.setTimeout(() => {
          setLabelStates((prev) =>
            prev[sampleId]?.state === 'saved' ? { ...prev, [sampleId]: { state: 'idle' } } : prev,
          );
        }, 2500);
      }
    },
    [],
  );
  /** Совместимость со старыми потребителями: «кто-то сохраняется» — первый из сохраняющихся. */
  const labelSavingId = useMemo(
    () => Object.entries(labelStates).find(([, v]) => v.state === 'saving')?.[0] ?? null,
    [labelStates],
  );
  const [labelAnnotateError, setLabelAnnotateError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [detectorBatch, setDetectorBatch] = useState<{
    state: 'idle' | 'running' | 'done' | 'error';
    outcome: CollectionDetectorBatchRunOutcome | null;
    error: string | null;
  }>({ state: 'idle', outcome: null, error: null });
  const [samplesPageByKey, setSamplesPageByKey] = useState<Record<string, number>>({});
  const [samplesPageLoading, setSamplesPageLoading] = useState(false);
  const [nodePageData, setNodePageData] = useState<PaginatedSamples | null>(null);
  const [playbackRowSnapshot, setPlaybackRowSnapshot] = useState<
    MediaSample | MembraneCatalogSample | null
  >(null);
  const { toast, dismiss, showError, showSuccess, showInfo } = useCabinetToast();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const me = await fetchMembraneMe();
      const id = me.membrane.id;
      setMembraneId(id);
      const nodesData = await fetchMembraneNodes(id);
      setNodes(nodesData.nodes);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const collectionPageKey = useMemo(() => {
    if (selection.kind === 'catalog') return 'catalog';
    if (selection.kind === 'node') {
      return `node:${selection.deviceId}:${selection.collectionId}`;
    }
    return null;
  }, [selection]);

  const samplesPage = collectionPageKey ? (samplesPageByKey[collectionPageKey] ?? 1) : 1;

  const setSamplesPage = useCallback(
    (page: number) => {
      if (!collectionPageKey) return;
      setSamplesPageByKey((prev) => ({ ...prev, [collectionPageKey]: page }));
    },
    [collectionPageKey],
  );

  /**
   * ЗАПОМНЕННАЯ СТРАНИЦА ПЕРЕЖИВАЕТ СПИСОК, О КОТОРОМ БЫЛА (#2505).
   *
   * Кабинет помнит номер страницы по набору (`samplesPageByKey`) — это удобно, пока набор не
   * ужался. Вывезли буфер окном «перенести все», удалили пачку, очистили буфер — страниц стало
   * меньше, а номер остался. Дверь честно отвечает на запрос: `items: []`, `page: 3`,
   * `totalPages: 1`. Дом при этом рисовал ПУСТУЮ таблицу, а органы листания скрываются при
   * `totalPages <= 1` — то есть «Назад» не оставалось вовсе, и выйти из пустоты было нечем.
   *
   * Поэтому ответ двери проверяется на связность с номером: номер вне диапазона приводится к
   * последней живой странице сразу, и эффект перезапрашивается уже за нею. Правило одно и живёт в
   * ядре библиотеки (`clampSamplesPage`) — второй копии в доме нет.
   */
  const reconcileSamplesPage = useCallback(
    (meta: { readonly page: number; readonly totalPages: number }) => {
      const fixed = clampSamplesPage(meta.page, meta.totalPages);
      if (fixed !== meta.page) setSamplesPage(fixed);
    },
    [setSamplesPage],
  );

  useEffect(() => {
    if (!membraneId || selection.kind !== 'catalog') return;
    let cancelled = false;
    setSamplesPageLoading(true);
    void fetchMembraneCatalog(membraneId, samplesPage, DEFAULT_SAMPLES_PAGE_SIZE)
      .then((data) => {
        if (cancelled) return;
        setCatalog(data);
        reconcileSamplesPage({ page: data.page, totalPages: data.totalPages });
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Ошибка загрузки каталога');
        }
      })
      .finally(() => {
        if (!cancelled) setSamplesPageLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [membraneId, reconcileSamplesPage, selection.kind, samplesPage]);

  const activeDeviceId = useMemo(() => {
    if (selection.kind === 'node') return selection.deviceId;
    if (selection.kind === 'catalog' && catalog?.sourceDeviceId) {
      return catalog.sourceDeviceId;
    }
    return null;
  }, [catalog?.sourceDeviceId, selection]);

  const activeNodeLabel = useMemo(() => {
    if (selection.kind === 'node') return selection.label;
    if (selection.kind === 'node-offline') return selection.label;
    return undefined;
  }, [selection]);

  const { snapshot, service, loading: libLoading, loadError, active, refresh } =
    useCabinetMediaLibrary(activeDeviceId, mediaReloadNonce);

  const playback = useSamplePlayback();

  /**
   * Сверка с сервером при входе в библиотеку узла (#2569): сервис кэширован по устройству на всю
   * SPA-сессию, и повторный вход давал пустой `init()` — серверное имя/состав базового набора не
   * доезжали до перезагрузки вкладки. Лёгкая сверка, пробы буфера не читаются.
   */
  useEffect(() => {
    if (!active) return;
    service.reconcileOnOpen().catch((err: unknown) => {
      console.error('[useCabinetSampleLibrary] reconcile on open failed', err);
    });
  }, [active, service]);

  useEffect(() => {
    if (selection.kind !== 'node' || !service || !active) {
      setNodePageData(null);
      return;
    }
    const collectionId = selection.collectionId;
    let cancelled = false;
    setSamplesPageLoading(true);
    void service
      .listSamplesPage(collectionId, samplesPage, DEFAULT_SAMPLES_PAGE_SIZE)
      .then((data) => {
        if (cancelled) return;
        setNodePageData(data);
        reconcileSamplesPage({ page: data.page, totalPages: data.totalPages });
      })
      .catch((e) => {
        if (!cancelled) {
          const msg = e instanceof Error ? e.message : String(e);
          showError(`Загрузка страницы: ${msg}`);
        }
      })
      .finally(() => {
        if (!cancelled) setSamplesPageLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [active, reconcileSamplesPage, samplesPage, selection, service, showError]);

  useEffect(() => {
    if (!playback.selectedSampleId) {
      setPlaybackRowSnapshot(null);
    }
  }, [playback.selectedSampleId]);

  useEffect(() => {
    if (!active || !service) return;
    bindSamplePlaybackBlobReader((sampleId: string) => service.getSampleBlob(sampleId));
    return () => {
      void disposeSamplePlayback();
    };
  }, [active, service]);

  const retryMediaLibrary = useCallback(() => {
    invalidateCabinetMediaLibrary();
    setMediaReloadNonce((n) => n + 1);
  }, []);

  const runMediaOp = useCallback(
    async (label: string, op: () => Promise<unknown>, retry?: () => void) => {
      if (!service || !active) {
        showError('Media-server недоступен', retryMediaLibrary);
        return;
      }
      setBusy(true);
      try {
        await runRemoteMutation(label, async () => {
          await op();
        });
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        showError(`${label}: ${msg}`, retry ?? (() => void runMediaOp(label, op, retry)));
      } finally {
        setBusy(false);
      }
    },
    [active, retryMediaLibrary, service, showError],
  );

  const toggleNodeExpanded = useCallback((nodeId: string) => {
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) next.delete(nodeId);
      else next.add(nodeId);
      return next;
    });
  }, []);

  const selectPairedNode = useCallback((node: MembraneNodeLibrary) => {
    if (!node.deviceId) return;
    setExpandedNodes((prev) => new Set(prev).add(node.id));
    setSelection({
      kind: 'node',
      nodeId: node.id,
      deviceId: node.deviceId,
      label: node.label,
      collectionId: BUFFER_COLLECTION_ID,
    });
  }, []);

  const selectOfflineNode = useCallback((node: MembraneNodeLibrary) => {
    setSelection({ kind: 'node-offline', nodeId: node.id, label: node.label });
  }, []);

  const catalogSamples = useMemo(() => catalog?.samples ?? [], [catalog?.samples]);
  const nodeSamples = useMemo(
    () => (selection.kind === 'node' ? (nodePageData?.items ?? []) : []),
    [nodePageData?.items, selection.kind],
  );

  /**
   * ПОЛНОЕ ЧИСЛО РЯДОМ С СТРАНИЦЕЙ (#2237). `nodeSamples` — страница, и это законно; беда
   * была в том, что компенсация («на самом деле их столько») жила у ВЫЗЫВАЮЩЕГО: окно
   * удаления её знало, а следующий потребитель источника про неё не узнал бы. Теперь
   * источник отдаёт обе величины сразу, и «сколько на экране» нельзя случайно принять за
   * «сколько есть».
   */
  const nodeSamplesTotal = useMemo(
    () => (selection.kind === 'node' ? (nodePageData?.total ?? nodeSamples.length) : 0),
    [nodePageData?.total, nodeSamples.length, selection.kind],
  );

  const reloadSamplesPage = useCallback(async () => {
    if (selection.kind === 'catalog' && membraneId) {
      const data = await fetchMembraneCatalog(
        membraneId,
        samplesPage,
        DEFAULT_SAMPLES_PAGE_SIZE,
      );
      setCatalog(data);
      return;
    }
    if (selection.kind === 'node' && service && active) {
      const data = await service.listSamplesPage(
        selection.collectionId,
        samplesPage,
        DEFAULT_SAMPLES_PAGE_SIZE,
      );
      setNodePageData(data);
    }
  }, [active, membraneId, samplesPage, selection, service]);

  const selectedCollection: Collection | undefined =
    selection.kind === 'node'
      ? snapshot.collections.find((c) => c.id === selection.collectionId)
      : undefined;

  useEffect(() => {
    setDetectorBatch({ state: 'idle', outcome: null, error: null });
  }, [selection.kind === 'node' ? selection.collectionId : selection.kind]);

  const isCatalogView = selection.kind === 'catalog';
  const isOfflineView = selection.kind === 'node-offline';
  const isNodeView = selection.kind === 'node';
  const catalogPlaybackBlocked = isCatalogView && !catalog?.sourceDeviceId;

  const samplesPagination = useMemo(() => {
    if (isCatalogView && catalog) {
      return {
        page: catalog.page,
        totalPages: catalog.totalPages,
        total: catalog.sampleCount,
        limit: catalog.limit,
      };
    }
    if (isNodeView && nodePageData) {
      return {
        page: nodePageData.page,
        totalPages: nodePageData.totalPages,
        total: nodePageData.total,
        limit: nodePageData.limit,
      };
    }
    return { page: 1, totalPages: 0, total: 0, limit: DEFAULT_SAMPLES_PAGE_SIZE };
  }, [catalog, isCatalogView, isNodeView, nodePageData]);

  const isTariffDataset =
    selectedCollection?.kind === 'system' &&
    selectedCollection.systemKey === TARIFF_DATASET_SYSTEM_KEY;

  // Правило одно и живёт в ядре (#2249). Прежняя редакция несла лишний частный случай
  // (`isTariffDataset ||`): тарифный набор И ЕСТЬ системный, то есть условие было
  // логически тем же, но объявленным иначе — и зуб близнецов закрепил оба написания.
  const readOnlyCollection = isReadOnlyCollection(selectedCollection);
  const quotaBlocked = active ? isQuotaFull(snapshot.quota) : false;
  const canMutate = isNodeView && active && !readOnlyCollection && !busy;
  const canLabelCatalog = isCatalogView && isAdmin && Boolean(membraneId);

  const handlePatchCatalogLabelNotes = useCallback(
    async (sampleId: string, patch: UpdateSampleLabelNotes) => {
      if (!membraneId || !canLabelCatalog) return;
      setLabelState(sampleId, { state: 'saving' });
      setLabelAnnotateError(null);
      try {
        const updated = await patchCatalogSample(membraneId, sampleId, patch);
        setCatalog((prev) =>
          prev
            ? {
                ...prev,
                samples: prev.samples.map((s) => (s.id === sampleId ? updated : s)),
              }
            : prev,
        );
        setLabelState(sampleId, { state: 'saved' });
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        setLabelAnnotateError(msg);
        setLabelState(sampleId, { state: 'error', detail: msg });
        showError(`Разметка: ${msg}`, () =>
          void handlePatchCatalogLabelNotes(sampleId, patch),
        );
      }
    },
    [canLabelCatalog, membraneId, setLabelState, showError],
  );

  const handlePatchNodeLabelNotes = useCallback(
    async (sampleId: string, patch: UpdateSampleLabelNotes) => {
      if (!service || !active || isTariffDataset) return;
      setLabelState(sampleId, { state: 'saving' });
      setLabelAnnotateError(null);
      try {
        const updated = await service.updateSampleLabelNotes(sampleId, patch);
        setNodePageData((prev) =>
          prev
            ? {
                ...prev,
                items: prev.items.map((s) => (s.id === sampleId ? updated : s)),
              }
            : prev,
        );
        // Успех — у строки («сохранено»), а не общим тостом: тост на каждую подпись при разборе
        // тысячи проб — шум, который заслоняет ошибки.
        setLabelState(sampleId, { state: 'saved' });
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        setLabelAnnotateError(msg);
        setLabelState(sampleId, { state: 'error', detail: msg });
        showError(`Разметка: ${msg}`, () => void handlePatchNodeLabelNotes(sampleId, patch));
      }
    },
    [active, isTariffDataset, service, setLabelState, showError],
  );

  const moveTargets = useMemo(
    () =>
      snapshot.collections.filter(
        (c) =>
          isNodeView &&
          selection.kind === 'node' &&
          c.id !== selection.collectionId &&
          c.kind !== 'buffer' &&
          c.kind !== 'system',
      ),
    [isNodeView, selection, snapshot.collections],
  );

  const selectedPlaybackSample = useMemo((): MediaSample | MembraneCatalogSample | null => {
    if (!playback.selectedSampleId) return null;
    if (isNodeView) {
      const onPage = nodeSamples.find((s) => s.id === playback.selectedSampleId);
      if (onPage) return onPage;
      return playbackRowSnapshot?.id === playback.selectedSampleId
        ? playbackRowSnapshot
        : null;
    }
    if (isCatalogView) {
      const onPage = catalogSamples.find((s) => s.id === playback.selectedSampleId);
      if (onPage) return onPage;
      return playbackRowSnapshot?.id === playback.selectedSampleId
        ? playbackRowSnapshot
        : null;
    }
    return null;
  }, [
    catalogSamples,
    isCatalogView,
    isNodeView,
    nodeSamples,
    playback.selectedSampleId,
    playbackRowSnapshot,
  ]);

  const playbackDisabled =
    busy || (isCatalogView ? catalogPlaybackBlocked || !active : !active);

  const handleSelectPlaybackSample = useCallback(
    async (row: MembraneCatalogSample | MediaSample, mode: 'catalog' | 'node') => {
      const media =
        mode === 'catalog' ? catalogSampleToMedia(row as MembraneCatalogSample) : (row as MediaSample);
      setPlaybackRowSnapshot(row);
      await selectSample(media);
    },
    [],
  );

  const handleTogglePlayback = useCallback(
    async (row: MembraneCatalogSample | MediaSample, mode: 'catalog' | 'node') => {
      const id = row.id;
      if (playback.selectedSampleId !== id) {
        await handleSelectPlaybackSample(row, mode);
      }
      await togglePlayPause();
    },
    [handleSelectPlaybackSample, playback.selectedSampleId],
  );

  const handleExportSelected = useCallback(async () => {
    if (!selectedPlaybackSample || !service) return;
    await runMediaOp('Экспорт', async () => {
      const blob = await service.getSampleBlob(selectedPlaybackSample.id);
      const ext = extensionFromMime(blob.type);
      downloadBlob(blob, `${selectedPlaybackSample.title}.${ext}`);
    });
  }, [runMediaOp, selectedPlaybackSample, service]);

  const handleCreateCollection = useCallback(async () => {
    const name = newCollectionName.trim();
    if (!name || selection.kind !== 'node') return;
    await runMediaOp('Создание коллекции', async () => {
      const col = await service!.createUserCollection(name);
      setNewCollectionName('');
      setSelection({ ...selection, collectionId: col.id });
      showSuccess(`Коллекция «${col.name}» создана`);
    });
  }, [newCollectionName, runMediaOp, selection, service, showSuccess]);

  const handleDeleteCollection = useCallback(async () => {
    if (!selectedCollection || selectedCollection.kind !== 'user' || selection.kind !== 'node') {
      return;
    }
    await runMediaOp('Удаление коллекции', async () => {
      await service!.deleteUserCollection(selectedCollection.id);
      setSelection({ ...selection, collectionId: BUFFER_COLLECTION_ID });
      showSuccess('Коллекция удалена');
    });
  }, [runMediaOp, selectedCollection, selection, service, showSuccess]);

  const handleImport = useCallback(
    async (file: File) => {
      if (!selectedCollection || selection.kind !== 'node' || readOnlyCollection) return;
      await runMediaOp('Импорт', async () => {
        await service!.importBlob(selection.collectionId, file, {
          title: file.name,
          class: DEFAULT_IMPORT_CLASS,
          label: 'unlabeled',
          source: 'disk-import',
          durationSec: 0,
          sampleRate: 48000,
        });
        showSuccess(`Импортирован: ${file.name}`);
      });
      await reloadSamplesPage();
    },
    [readOnlyCollection, reloadSamplesPage, runMediaOp, selectedCollection, selection, service, showSuccess],
  );

  const handleRemove = useCallback(
    async (sampleId: string) => {
      if (readOnlyCollection) return;
      await runMediaOp('Удаление сэмпла', async () => {
        await service!.removeSample(sampleId);
        if (playback.selectedSampleId === sampleId) await selectSample(null);
        showSuccess('Сэмпл удалён');
      });
      await reloadSamplesPage();
    },
    [playback.selectedSampleId, readOnlyCollection, reloadSamplesPage, runMediaOp, service, showSuccess],
  );

  const handleMove = useCallback(
    async (sampleId: string, toId: string) => {
      if (!toId || readOnlyCollection) return;
      // Перенос говорит, КУДА едет (#2110): «переносится в …» пока идёт, «перенесено в …» по
      // исходу. Прежде тост знал, что перенесён, но не знал куда.
      //
      // Слово написано по АДРЕСАТУ, а не по источнику, поэтому перенос набор→набор говорит им
      // без единой правки — близнец в Studio устроен так же.
      const targetName = snapshot.collections.find((c) => c.id === toId)?.name ?? toId;
      showInfo(`Переносится в «${targetName}»…`);
      await runMediaOp('Перенос сэмпла', async () => {
        await service!.moveSample(sampleId, toId);
        showSuccess(`Перенесено в «${targetName}»`);
      });
      await reloadSamplesPage();
    },
    [readOnlyCollection, reloadSamplesPage, runMediaOp, service, showInfo, showSuccess, snapshot.collections],
  );

  /**
   * Удаление по списку (#2250). Один вызов на всю пачку, а не цикл: цикл дал бы N запросов,
   * N возможных полу-исходов и ни одного общего отчёта.
   *
   * ЧАСТИЧНЫЙ ОТКАЗ ГОВОРИТ СЛОВАМИ и ПОИМЁННО — сервер вправе удалить часть и отказать по
   * части. Молчание здесь хуже ошибки: человек считал бы удалённым то, что осталось.
   * Близнец в Studio устроен так же.
   */
  const handleRemoveMany = useCallback(
    async (sampleIds: readonly string[]) => {
      if (readOnlyCollection || sampleIds.length === 0 || selection.kind !== 'node') return;
      const collectionId = selection.collectionId;
      await runMediaOp('Удаление выбранных', async () => {
        const outcome = await service!.deleteSamplesByIds(collectionId, sampleIds);
        if (outcome.refused.length > 0) {
          const named = outcome.refused
            .map((r) => `${nodeSamples.find((s) => s.id === r.id)?.title ?? r.id} — ${r.why}`)
            .join('; ');
          showError(`Удалено ${outcome.deleted}, отказано ${outcome.refused.length}: ${named}`);
        } else {
          showSuccess(`Удалено ${outcome.deleted}`);
        }
      });
      await reloadSamplesPage();
    },
    [
      nodeSamples,
      readOnlyCollection,
      reloadSamplesPage,
      runMediaOp,
      selection,
      service,
      showError,
      showSuccess,
    ],
  );

  /**
   * ПЕРЕНОС ПАЧКОЙ (заказ владельца 27.09). Близнец Studio: окно, план, подтверждение.
   *
   * Перечисление идёт ПОЛНЫМ списком набора (`listSamples` бэкенда обходит все страницы), а
   * НЕ по `nodeSamples`: в руках кабинета лежит
   * СТРАНИЦА (40 из 1057), и перенос по ней уехал бы сороковкой — с виду успешно. Это тот
   * самый класс решений по видимому вместо существующего (`docs/field/decisions-on-partial-data.md`).
   *
   * Недоступность media названа словами и здесь: порт обязан ОТКАЗАТЬ, а не вернуть пустой
   * перечень — пустой перечень окно прочло бы как «в наборе нет проб».
   */
  const moveAllPort = useMemo<MoveAllPort>(
    () => ({
      enumerate: async () => {
        if (!service || !active || selection.kind !== 'node') {
          throw new Error('Media-server недоступен — перечислить пробы набора нечем.');
        }
        const all = await service.getBackend().listSamples(selection.collectionId);
        return all.map((s) => s.id);
      },
      run: async (sampleIds, toCollectionId, options) => {
        if (!service || !active) {
          throw new Error('Media-server недоступен — перенос невозможен.');
        }
        return service.moveSamplesBatch(sampleIds, toCollectionId, options);
      },
    }),
    [active, selection, service],
  );

  /**
   * Адрес источника для МАССОВОГО переноса: у вида «каталог»/«узел офлайн» набора нет, и
   * буфером такой выбор не бывает. Один вывод на два потребителя — предикат кнопки и
   * `source.isBuffer` окна, — чтобы они не разошлись двумя написаниями одного признака.
   */
  const moveAllSourceId = selection.kind === 'node' ? selection.collectionId : null;
  const sourceIsBuffer = isMoveAllSourceBuffer(moveAllSourceId);

  /**
   * МАССОВЫЙ перенос — только из буфера (слово владельца 27.09): дверь возит пачкой только
   * оттуда. Правило не объявляется здесь заново, его несёт носитель окна — один на два дома.
   * Построчный перенос остаётся на `canMutate`, из любого набора (#2249).
   */
  const canMoveAll = canMutate && canOfferMoveAll(moveAllSourceId, moveTargets);

  const handleClearBuffer = useCallback(async () => {
    if (selection.kind !== 'node') return;
    await runMediaOp('Очистка буфера', async () => {
      await service!.clearBuffer();
      await selectSample(null);
      showSuccess('Буфер очищен');
    });
    await reloadSamplesPage();
  }, [reloadSamplesPage, runMediaOp, selection, service, showSuccess]);

  const handleRunDetectorBatch = useCallback(async () => {
    if (!service || !active || selection.kind !== 'node') {
      const message = 'Media-server недоступен — batch-прогон детекторов невозможен.';
      setDetectorBatch({ state: 'error', outcome: null, error: message });
      showError(message, retryMediaLibrary);
      return;
    }
    setDetectorBatch({ state: 'running', outcome: null, error: null });
    try {
      const outcome = await service.requestCollectionDetectorBatch(selection.collectionId);
      setDetectorBatch({ state: 'done', outcome, error: null });
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      setDetectorBatch({ state: 'error', outcome: null, error: message });
      showError(`Прогон детекторов: ${message}`, () => void handleRunDetectorBatch());
    }
  }, [active, retryMediaLibrary, selection, service, showError]);

  const handleExport = useCallback(
    async (sample: MediaSample) => {
      await runMediaOp('Экспорт', async () => {
        const blob = await service!.getSampleBlob(sample.id);
        const ext = extensionFromMime(blob.type);
        downloadBlob(blob, `${sample.title}.${ext}`);
      });
    },
    [runMediaOp, service],
  );

  return {
    loading,
    error,
    load,
    membraneId,
    catalog,
    nodes,
    selection,
    setSelection,
    expandedNodes,
    newCollectionName,
    setNewCollectionName,
    busy,
    toast,
    dismiss,
    loadError,
    libLoading,
    snapshot,
    active,
    refresh,
    playback,
    retryMediaLibrary,
    toggleNodeExpanded,
    selectPairedNode,
    selectOfflineNode,
    catalogSamples,
    nodeSamples,
    nodeSamplesTotal,
    selectedCollection,
    isCatalogView,
    isOfflineView,
    isNodeView,
    isTariffDataset,
    catalogPlaybackBlocked,
    readOnlyCollection,
    quotaBlocked,
    canMutate,
    moveTargets,
    canMoveAll,
    sourceIsBuffer,
    moveAllPort,
    /** Перечитать страницу проб — окну переноса после удачного прогона. */
    reloadSamplesPage,
    selectedPlaybackSample,
    playbackDisabled,
    activeNodeLabel,
    handleSelectPlaybackSample,
    handleTogglePlayback,
    handleExportSelected,
    handleCreateCollection,
    handleDeleteCollection,
    handleImport,
    handleRemove,
    handleRemoveMany,
    handleMove,
    handleClearBuffer,
    detectorBatch,
    handleRunDetectorBatch,
    handleExport,
    isAdmin,
    canLabelCatalog,
    /** Сервис библиотеки узла — для панели отбора: тот же транспорт, что читает пробы. */
    service,
    labelSavingId,
    labelStates,
    labelAnnotateError,
    handlePatchCatalogLabelNotes,
    handlePatchNodeLabelNotes,
    samplesPage,
    setSamplesPage,
    samplesPageLoading,
    samplesPagination,
  };
}

export type CabinetSampleLibraryModel = ReturnType<typeof useCabinetSampleLibrary>;
